// at top: keep imports
import React, { useEffect, useState, useRef, useLayoutEffect, memo } from 'react';
import Document_Preview from "./Document_Preview";

export default function Document_Renderer({ Type, Orientation, Display, Subtitle, Presentation, children }) {

    const [Components, Components_Set] = useState([]);
    const [Pages, Pages_Set] = useState([]); // Final render output
    const [Render_State, Render_State_Set] = useState("Initial");
    const [Progress, Progress_Set] = useState(0);

    const TesterRef = useRef(null);
    // let Height_Limit = 705;
    let Height_Limit = 640;
    if (Orientation === "Landscape") Height_Limit = 545;

    // New flag: we need to measure
    const [MeasurePending, MeasurePending_Set] = useState(false);

    useEffect(() => {
        if (Presentation === "Normal") return;

        // Convert children to stable elements and give them stable keys.
        // If your child elements already have unique keys (preferred), this is redundant.
        // const elements = React.Children.toArray(children).map((child, idx) =>
        //     React.cloneElement(child, { key: child.key ?? (child.props?.id ?? `doc-${idx}`) })
        // );
        const elements = React.Children.toArray(children).map((child, idx) => {
            if (!React.isValidElement(child)) return null;
            return React.cloneElement(child, { key: child.key ?? (child.props?.id ?? `doc-${idx}`) });
        });

        Components_Set(elements);
        Pages_Set([]);
        Render_State_Set("Initial");
        Progress_Set(0);

        // kick off measurement pass
        MeasurePending_Set(true);

    }, [children, Presentation, Orientation]);

    // Do the measurement in a layout effect (runs before paint)
    useLayoutEffect(() => {
        if (!Display) return;
        if (!MeasurePending) return;
        if (!TesterRef.current) return;

        // Ensure tester rendered all components (we'll render them in the DOM marked wrappers)
        const node = TesterRef.current;

        // Give the browser one RAF to ensure layout is stable, especially if UI just changed.
        requestAnimationFrame(() => {
            const wrappers = node.querySelectorAll('.document-measure-child');

            if (!wrappers || wrappers.length === 0) {
                // nothing to measure
                MeasurePending_Set(false);
                return;
            }

            const heights = Array.from(wrappers).map(w => {
                // offsetHeight is safer for measured layout; scrollHeight also okay
                return w.offsetHeight || w.scrollHeight || 0;
            });

            // Build pages in a single pass without setState in the loop.
            const pages = [];
            let currentPage = [];
            let currentHeight = 0;

            heights.forEach((h, i) => {
                const element = Components[i]; // Components array aligns with wrappers by index
                if (currentHeight + h <= Height_Limit) {
                    currentPage.push(element);
                    currentHeight += h;
                } else {
                    // push current page and start new one
                    pages.push([...currentPage]);
                    currentPage = [element];
                    currentHeight = h;
                }

                // update progress as we go (but we will set state once)
                const prog = ((i + 1) / heights.length) * 100;
                Progress_Set(Number(prog.toFixed(1)));
            });

            // push last page
            if (currentPage.length > 0) pages.push([...currentPage]);

            // Commit results once
            Pages_Set(pages);
            Render_State_Set("Active");
            MeasurePending_Set(false);

            console.info("Pagination complete:", pages.length, "pages");
        });

    }, [MeasurePending, Display, Components, Height_Limit]);

    // if Display toggles off, clear
    useEffect(() => {
        if (Display === false) {
            Render_State_Set("Initial");
            Pages_Set([]);
            Components_Set([]);
            MeasurePending_Set(false);
            Progress_Set(0);
        }
    }, [Display]);

    // Render
    if (Presentation === "Normal") {
        return <div className='Document_Normal'>{children}</div>;
    }

    return (
        <>
            <div className='Document_Initial' State={Render_State === "Active" ? "Inactive" : "Active"}>
                <h3 className='General_Paragraph'>No document to render</h3>
            </div>

            <div className='Document_Loading' State={Render_State === "Loading" || MeasurePending ? "Active" : "Inactive"}>
                <h3 className='General_Paragraph'>Rendering document ({Progress}%)</h3>
            </div>

            {/* HIDDEN TESTER: Render ALL components wrapped so we can measure in one pass */}
            <div
                className="Document_Tester"
                style={{
                    // keep it offscreen and not affecting layout. Make sure width matches final preview width.
                    position: "absolute",
                    left: "-99999px",
                    top: "0",
                    width: "794px", // set to your final preview width (e.g. Letter portrait width in px)
                    visibility: "hidden",
                    pointerEvents: "none",
                    overflow: "auto"
                }}
            >
                <div id="Document_Tester" ref={TesterRef}>
                    {Components.map((Comp, idx) => (
                        <div className="document-measure-child" key={`measure-${idx}`} style={{ display: "block" }}>
                            {Comp}
                        </div>
                    ))}
                </div>
            </div>

            {/* Final pages */}
            {Pages.map((Page, Index) => (
                <Document_Preview key={Index} Type={Type} Page={Index + 1} Page_Total={Pages.length} Orientation={Orientation} Subtitle={Subtitle}>
                    {Page.map((Component, CIndex) => (
                        <React.Fragment key={CIndex}>{Component}</React.Fragment>
                    ))}
                </Document_Preview>
            ))}
        </>
    );
}
